import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Briefcase } from 'lucide-react';
import { handleFormatAmount } from '@/utility/helper';

interface SalaryBreakdownSummaryRow {
    label: string;
    description: string;
    monthlyAmount: number | string;
    yearlyAmount: number | string;
}

interface SalaryBreakdownSummaryTableProps {
    basicSalaryAmount?: number;
    allowancesAmount?: number;
    grossSalaryAmount?: number;
    totalDeductionsAmount?: number;
    netPayAmount?: number;
    items?: SalaryBreakdownSummaryRow[];
}

const SalaryBreakdownSummaryTable: React.FC<SalaryBreakdownSummaryTableProps> = ({
    basicSalaryAmount = 0,
    allowancesAmount = 0,
    grossSalaryAmount = 0,
    totalDeductionsAmount = 0,
    netPayAmount = 0,
    items,
}) => {
    return (
        <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm bg-white">
            <div className="px-4 py-3.5 border-b border-slate-100 bg-slate-50/40 flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-cyan-600 animate-ping"></div>
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-cyan-600" />
                    Salary Breakdown Summary
                </h4>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader className="bg-slate-50/60">
                        <TableRow className="border-b border-slate-100">
                            <TableHead className="font-semibold text-slate-600 pl-4 h-11 text-xs">Label</TableHead>
                            <TableHead className="font-semibold text-slate-600 h-11 text-xs">Description</TableHead>
                            <TableHead className="font-semibold text-slate-600 text-right h-11 text-xs">Monthly Amount</TableHead>
                            <TableHead className="font-semibold text-slate-600 text-right pr-4 h-11 text-xs">Yearly Amount</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-slate-100">
                        {items ? (
                            items.map((item, idx) => {
                                const isCTC = item.label?.toLowerCase() === 'ctc';
                                const isGross = item.label?.toLowerCase() === 'gross income' || item.label?.toLowerCase() === 'gross salary';
                                const isDeductions = item.label?.toLowerCase() === 'total deductions';
                                const isNetPay = item.label?.toLowerCase() === 'net pay' || item.label?.toLowerCase() === 'net salary';

                                let textStyle = "font-semibold text-slate-800 text-xs";
                                let amountStyle = "text-right font-semibold text-xs text-slate-600";
                                let bgClass = "hover:bg-slate-50/30 border-b border-slate-100";

                                if (isCTC) {
                                    textStyle = "font-extrabold text-cyan-900 text-xs";
                                    amountStyle = "text-right font-extrabold text-cyan-700 text-xs";
                                    bgClass = "bg-cyan-50/35 hover:bg-cyan-50/50 transition-colors";
                                } else if (isGross) {
                                    amountStyle = "text-right font-semibold text-emerald-600 text-xs";
                                } else if (isDeductions) {
                                    amountStyle = "text-right font-semibold text-rose-600 text-xs";
                                } else if (isNetPay) {
                                    amountStyle = "text-right font-semibold text-cyan-600 text-xs";
                                }

                                return (
                                    <TableRow key={idx} className={bgClass}>
                                        <TableCell className={`${textStyle} pl-4 py-3.5`}>{item.label}</TableCell>
                                        <TableCell className="text-slate-500 py-3.5 text-xs">{item.description}</TableCell>
                                        <TableCell className={`${amountStyle} py-3.5`}>
                                            {handleFormatAmount(item.monthlyAmount)}
                                        </TableCell>
                                        <TableCell className={`${amountStyle} pr-4 py-3.5`}>
                                            {handleFormatAmount(item.yearlyAmount)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <>
                                <TableRow className="hover:bg-slate-50/30 border-b border-slate-100">
                                    <TableCell className="font-semibold text-slate-800 pl-4 py-3.5 text-xs">Gross Income</TableCell>
                                    <TableCell className="text-slate-500 py-3.5 text-xs">
                                        Basic <span className='text-cyan-600 font-semibold'> ({handleFormatAmount(basicSalaryAmount)})</span> +  Allowances <span className='text-orange-600 font-semibold'> ({handleFormatAmount(allowancesAmount)})</span>
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-emerald-600 py-3.5 text-xs">
                                        {handleFormatAmount(grossSalaryAmount)}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-emerald-600 pr-4 py-3.5 text-xs">
                                        {handleFormatAmount(grossSalaryAmount * 12)}
                                    </TableCell>
                                </TableRow>
                                <TableRow className="hover:bg-slate-50/30 border-b border-slate-100">
                                    <TableCell className="font-semibold text-slate-800 pl-4 py-3.5 text-xs">Total Deductions</TableCell>
                                    <TableCell className="text-slate-500 py-3.5 text-xs">From Deductions breakdown</TableCell>
                                    <TableCell className="text-right font-semibold text-rose-600 py-3.5 text-xs">
                                        {handleFormatAmount(totalDeductionsAmount)}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-rose-600 pr-4 py-3.5 text-xs">
                                        {handleFormatAmount(totalDeductionsAmount * 12)}
                                    </TableCell>
                                </TableRow>
                                <TableRow className="hover:bg-slate-50/30 border-b border-slate-100">
                                    <TableCell className="font-semibold text-slate-800 pl-4 py-3.5 text-xs">Net Pay</TableCell>
                                    <TableCell className="text-slate-500 py-3.5 text-xs">Gross Income - Deductions</TableCell>
                                    <TableCell className="text-right font-semibold text-cyan-600 py-3.5 text-xs">
                                        {handleFormatAmount(netPayAmount)}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-cyan-600 pr-4 py-3.5 text-xs">
                                        {handleFormatAmount(netPayAmount * 12)}
                                    </TableCell>
                                </TableRow>
                                <TableRow className="bg-cyan-50/35 hover:bg-cyan-50/50 transition-colors">
                                    <TableCell className="font-extrabold text-cyan-900 pl-4 py-4 text-xs">CTC</TableCell>
                                    <TableCell className="text-cyan-800 font-medium py-4 text-xs">Gross Income + Employer Contribution</TableCell>
                                    <TableCell className="text-right font-extrabold text-cyan-700 py-4 text-xs">
                                        {handleFormatAmount(grossSalaryAmount)}
                                    </TableCell>
                                    <TableCell className="text-right font-extrabold text-cyan-700 pr-4 py-4 text-xs">
                                        {handleFormatAmount(grossSalaryAmount * 12)}
                                    </TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default SalaryBreakdownSummaryTable;
